/**
 * MyOPlus Testimonials Section
 * Handles testimonial row visibility and UGC carousel initialization
 */

(function () {
  'use strict';

  const BREAKPOINT_DESKTOP = 1024;
  const SWIPER_POLL_INTERVAL = 50;
  const CAROUSEL_CENTER_DELAY = 100;
  const SECTION_REINIT_DELAY = 100;
  const UGC_VIDEO_PRELOAD_MARGIN = '50px';

  /**
   * Debounce utility function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Initialize testimonial rows visibility based on viewport
   * @param {HTMLElement} container - Testimonials container element
   */
  function initTestimonialRows(container) {
    const row1 = container.querySelector('.myoplus-testimonials__row--1');
    const row2 = container.querySelector('.myoplus-testimonials__row--2');

    if (!row2) return;

    function calculateRows() {
      const isMobile = window.innerWidth < BREAKPOINT_DESKTOP;
      
      if (isMobile) {
        if (row2.dataset.hasContent === 'true') {
          row2.style.display = 'none';
        }
        return;
      }

      if (row2.dataset.hasContent === 'true') {
        row2.style.display = 'flex';
      }
    }

    calculateRows();
    
    const debouncedCalculateRows = debounce(calculateRows, 150);
    window.addEventListener('resize', debouncedCalculateRows);
  }

  /**
   * Center UGC carousel if all slides fit within container
   * @param {HTMLElement} carousel - Swiper carousel element
   */
  function centerCarousel(carousel) {
    const slides = carousel.querySelectorAll('.swiper-slide');
    const slideCount = slides.length;
    
    if (slideCount === 0) return;

    const isMobile = window.innerWidth < BREAKPOINT_DESKTOP;
    const slideWidth = isMobile ? 144 : 234;
    const gap = isMobile ? 16 : 32;
    const totalWidth = slideCount * slideWidth + (slideCount - 1) * gap;
    const containerWidth = carousel.offsetWidth;

    if (totalWidth < containerWidth) {
      const offset = (containerWidth - totalWidth) / 2;
      const wrapper = carousel.querySelector('.swiper-wrapper');
      if (wrapper) {
        wrapper.style.marginLeft = `${offset}px`;
      }
    } else {
      const wrapper = carousel.querySelector('.swiper-wrapper');
      if (wrapper) {
        wrapper.style.marginLeft = '0';
      }
    }
  }

  /**
   * Initialize video lazy loading and play/pause handlers
   * @param {HTMLElement} carousel - Swiper carousel element
   */
  function initUGCVideos(carousel) {
    const ugcItems = carousel.querySelectorAll('.myoplus-testimonials__ugc-item');
    
    ugcItems.forEach((item) => {
      const video = item.querySelector('video');
      if (!video) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              video.load();
            }
          });
        },
        { rootMargin: UGC_VIDEO_PRELOAD_MARGIN }
      );

      observer.observe(item);

      item.addEventListener('click', (e) => {
        e.stopPropagation();
        
        if (video.readyState >= 2) {
          if (video.paused) {
            video.play().catch(() => {
              // Handle play promise rejection silently
            });
          } else {
            video.pause();
          }
        } else {
          video.load();
          video.play().catch(() => {
            // Handle play promise rejection silently
          });
        }
      });
    });
  }

  /**
   * Initialize Swiper carousel for UGC content
   * @param {HTMLElement} carousel - Carousel container element
   * @returns {boolean} Success status
   */
  function initCarousel(carousel) {
    if (typeof Swiper === 'undefined') {
      return false;
    }

    try {
      const isMobile = window.innerWidth < BREAKPOINT_DESKTOP;
      const slides = carousel.querySelectorAll('.swiper-slide');
      const slideCount = slides.length;
      
      const swiper = new Swiper(carousel, {
        slidesPerView: 'auto',
        spaceBetween: isMobile ? 16 : 32,
        loop: false,
        centeredSlides: true,
        centeredSlidesBounds: isMobile ? false : true,
        initialSlide: isMobile && slideCount > 0 ? Math.floor(slideCount / 2) : 0,
        freeMode: true,
        freeModeMomentum: true,
        freeModeSticky: false,
        freeModeMinimumVelocity: 0.02,
        resistance: true,
        resistanceRatio: 0.85,
        speed: 300,
        grabCursor: true,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        touchEventsTarget: 'container',
        preventInteractionOnTransition: false,
      });

      swiper.on('init', () => {
        centerCarousel(carousel);
        if (isMobile && slideCount > 0) {
          setTimeout(() => {
            swiper.slideTo(Math.floor(slideCount / 2), 0);
          }, 50);
        }
      });
      
      swiper.on('resize', () => {
        const isMobile = window.innerWidth < BREAKPOINT_DESKTOP;
        swiper.params.spaceBetween = isMobile ? 16 : 32;
        swiper.update();
        centerCarousel(carousel);
        if (isMobile && slideCount > 0) {
          setTimeout(() => {
            swiper.slideTo(Math.floor(slideCount / 2), 0);
          }, 50);
        }
      });
      
      setTimeout(() => {
        centerCarousel(carousel);
        if (isMobile && slideCount > 0) {
          swiper.slideTo(Math.floor(slideCount / 2), 0);
        }
      }, CAROUSEL_CENTER_DELAY);
      
      initUGCVideos(carousel);

      return true;
    } catch (error) {
      console.error('Error initializing Swiper carousel:', error);
      return false;
    }
  }

  /**
   * Wait for Swiper library to load, then initialize carousel
   * @param {HTMLElement} carousel - Carousel container element
   */
  function waitForSwiper(carousel) {
    if (initCarousel(carousel)) {
      return;
    }
    setTimeout(() => waitForSwiper(carousel), SWIPER_POLL_INTERVAL);
  }

  /**
   * Initialize testimonials section
   * @param {string} sectionId - Section ID
   */
  function initTestimonials(sectionId) {
    const container = document.querySelector(
      `#shopify-section-${sectionId} .myoplus-testimonials__container`
    );
    
    if (!container) {
      console.error('Testimonials container not found for section:', sectionId);
      return;
    }

    initTestimonialRows(container);

    const ugcCarousel = document.querySelector(`#ugc-carousel-${sectionId}`);
    if (ugcCarousel) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => waitForSwiper(ugcCarousel));
      } else {
        waitForSwiper(ugcCarousel);
      }
    }
  }

  /**
   * Initialize all testimonials sections on page load
   */
  function initAllSections() {
    const sections = document.querySelectorAll('[data-testimonials-section]');
    sections.forEach((section) => {
      const sectionId = section.dataset.testimonialsSection;
      if (sectionId) {
        initTestimonials(sectionId);
      }
    });
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSections);
  } else {
    initAllSections();
  }

  // Reinitialize on Shopify section load (Theme Editor)
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail?.sectionId) {
      const section = document.querySelector(
        `#shopify-section-${event.detail.sectionId}[data-testimonials-section]`
      );
      if (section) {
        setTimeout(() => {
          initTestimonials(event.detail.sectionId);
        }, SECTION_REINIT_DELAY);
      }
    }
  });
})();
